import React from "react";
import type { Feature, Geometry, FeatureCollection } from "geojson";

type HistogramBin = { label: string; count: number; min?: number; max?: number; color?: string; };

interface Props {
  // vector
  geoData: FeatureCollection<Geometry, any> | null;
  filteredFeatures: Feature<Geometry, any>[];
  chartData: any[];
  histogramData?: HistogramBin[];
  isContinuous: boolean;
  isDegraded: boolean;

  // general
  selectedFile: string;
  selectedYear?: string;
  LandCoverSubType?: string;
  wetlandSubType?: string;
  forestSubType?: string;
  selectedSubcatchment?: string;
  selectedLULCSubcatchment?: string;
  chartProperty?: string;
  countryIso?: string;

  // 🧪 soil raster-specific
  selectedRaster?: string;                        // URL to the COG/GeoTIFF
  soilSubType?: string;                           // e.g. "pH"
  rasterRef?: React.MutableRefObject<any> | null; // ref to your RasterLayer instance

  className?: string;
  enableRasterPNG?: boolean; 
}

const slug = (s?: string) =>
  (s || "na").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

const escapeCSV = (val: any) => {
  if (val == null) return "";
  const s = String(val).replace(/\r?\n|\r/g, " ");
  return /[",]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadBlob = (data: BlobPart, filename: string, mime: string) => {
  const blob = new Blob([data], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const DataDownload: React.FC<Props> = ({
  // vector
  geoData,
  filteredFeatures,
  chartData,
  histogramData,
  isContinuous,
  isDegraded,

  // general
  selectedFile,
  selectedYear,
  LandCoverSubType,
  wetlandSubType,
  forestSubType,
  selectedSubcatchment,
  selectedLULCSubcatchment,
  chartProperty,
  countryIso = "lso",

  // soil raster
  selectedRaster,
  soilSubType,
  rasterRef,
  enableRasterPNG = false,

  className,
}) => {
  const hasFeatures =
    !!geoData &&
    ((filteredFeatures?.length || 0) > 0 ||
      (geoData.features?.length || 0) > 0);

  const canExportChart =
    (selectedFile === "Soil" && (histogramData?.length || 0) > 0) ||
    (Array.isArray(chartData) && chartData.length > 0);

  const makeBaseName = () => {
    const subType =
      selectedFile === "Landcover"
        ? LandCoverSubType
        : selectedFile === "Wetland"
        ? wetlandSubType
        : selectedFile === "Forest"
        ? forestSubType
        : selectedFile === "Soil"
        ? soilSubType
        : "";
    const subcatch = selectedSubcatchment || selectedLULCSubcatchment || "all";
    const parts = [
      slug(countryIso),
      slug(selectedFile || "layer"),
      slug(subType || ""),
      selectedYear || "",
      slug(subcatch),
      chartProperty ? `by_${slug(chartProperty)}` : "",
    ].filter(Boolean);
    return parts.join("_");
  };

  // ------- VECTOR EXPORTS -------
  const exportFilteredGeoJSON = () => {
    if (!geoData) return;
    const feats =
      (filteredFeatures && filteredFeatures.length > 0
        ? filteredFeatures
        : geoData.features || []) as Feature<Geometry, any>[];
    const fc: FeatureCollection<Geometry, any> = { type: "FeatureCollection", features: feats };
    downloadBlob(JSON.stringify(fc), `${makeBaseName()}.geojson`, "application/geo+json");
  };

  const exportFilteredCSV = () => {
    if (!geoData) return;
    const feats =
      (filteredFeatures && filteredFeatures.length > 0
        ? filteredFeatures
        : geoData.features || []) as Feature<Geometry, any>[];
    if (feats.length === 0) return;

    const allKeys = Array.from(new Set(feats.flatMap((f: any) => Object.keys(f.properties || {}))));
    const header = allKeys.map(escapeCSV).join(",");
    const rows = feats.map((f: any) => allKeys.map((k) => escapeCSV(f.properties?.[k])).join(","));
    const csv = [header, ...rows].join("\n");
    downloadBlob(csv, `${makeBaseName()}_features.csv`, "text/csv");
  };

  const exportChartCSV = () => {
    if (selectedFile === "Soil" && histogramData?.length) {
      const header = ["label", "count", "min", "max"];
      const rows = histogramData.map((b) =>
        [b.label, b.count, b.min, b.max].map(escapeCSV).join(",")
      );
      const csv = [header.join(","), ...rows].join("\n");
      downloadBlob(csv, `${makeBaseName()}_histogram.csv`, "text/csv");
      return;
    }
    if (!chartData || chartData.length === 0) return;
    const isRowArray = Array.isArray(chartData[0]);

    let csv: string;
    if (isContinuous && isRowArray) {
      const rows = (chartData as any[]).map((row) => (row as any[]).map(escapeCSV).join(","));
      csv = rows.join("\n");
    } else {
      const rows = [["name", "value"], ...((chartData as any[]).map((d: any) => [d.name, d.value]))];
      csv = rows.map((r) => r.map(escapeCSV).join(",")).join("\n");
    }
    const what = isDegraded ? "area_by_category" : "counts_by_category";
    downloadBlob(csv, `${makeBaseName()}_${what}.csv`, "text/csv");
  };

  // ------- SOIL RASTER EXPORTS -------
  // 1) Download original GeoTIFF/COG referenced by selectedRaster
  const downloadSoilGeoTIFF = async () => {
    if (!selectedRaster) return;
    try {
      const res = await fetch(selectedRaster, { mode: "cors" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const base = makeBaseName() || "soil_raster";
      downloadBlob(blob, `${base}.tif`, "image/tiff");
    } catch (err) {
      console.error("Raster download failed:", err);
      alert("Could not download the raster. If this is a CORS issue, host it with Access-Control-Allow-Origin: * or proxy the request.");
    }
  };

  // 2) Export the currently rendered raster canvas as PNG (quick-look)
  const exportSoilPNG = () => {
    const layer = rasterRef?.current;
    // Works for georaster-layer-for-leaflet: layer._canvas or layer.getCanvas?.()
    const canvas: HTMLCanvasElement | undefined = layer?._canvas || layer?.getCanvas?.();
    if (!canvas) {
      alert("Raster canvas is not available yet. Try again after the layer finishes rendering.");
      return;
    }
    // NOTE: Requires CORS-enabled tiles/files to avoid a tainted canvas.
    canvas.toBlob((blob) => {
      if (!blob) return;
      const base = makeBaseName() || "soil_raster_view";
      downloadBlob(blob, `${base}.png`, "image/png");
    });
  };

  const showRasterButtons = selectedFile === "Soil" && !!selectedRaster;

  if (!hasFeatures && !canExportChart && !showRasterButtons) return null;

  return (
    <div className={className || "download-panel"} style={{ marginTop: 16 }}>
      <h3>Download</h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {/* Vector (if present) */}
        {hasFeatures && (
          <>
            <button onClick={exportFilteredGeoJSON}>GeoJSON (filtered)</button>
            <button onClick={exportFilteredCSV}>CSV (filtered attributes)</button>
            <button onClick={exportChartCSV} disabled={!canExportChart}>
              CSV ({selectedFile === "Soil" ? "histogram" : "current chart"})
            </button>
          </>
        )}

        {/* Soil raster */}
        {showRasterButtons && (
          <>
            <hr />
            <strong>Soil raster</strong>
            <button onClick={downloadSoilGeoTIFF}>GeoTIFF (original)</button>
            <button onClick={exportSoilPNG} disabled={!enableRasterPNG}>PNG (current view)</button>
          </>
        )}
      </div>
    </div>
  );
};

export default DataDownload;
